import { Component, Input, OnChanges } from '@angular/core';
import { TeamsService, MonthlyTopupSchedulePreview } from '../../services/teams/teams.service';

@Component({
  selector: 'app-monthly-topup-schedule',
  templateUrl: './monthly-topup-schedule.component.html',
})
export class MonthlyTopupScheduleComponent implements OnChanges {
  @Input() teamId?: string;
  @Input() disabled = false;
  readonly days = Array.from({ length: 28 }, (_, i) => i + 1);
  day?: number;
  currentDay?: number;
  nextFundingDate: string | null = null;
  hasSubscription = false;
  pending = false;
  loading = false;
  error = '';
  message = '';
  preview: MonthlyTopupSchedulePreview | null = null;

  get hasUnsavedDay(): boolean {
    return this.day !== undefined && this.day !== this.currentDay;
  }

  get fundingBlocked(): boolean {
    return this.loading || !!this.error || this.pending || this.hasUnsavedDay;
  }

  constructor(private readonly teams: TeamsService) {}

  ngOnChanges(): void {
    void this.load();
  }

  async load(): Promise<void> {
    const teamId = this.teamId;
    this.preview = null;
    this.error = '';
    this.message = '';
    if (!teamId) return;
    this.loading = true;
    try {
      const response = await this.teams.getMonthlyTopupSchedule(teamId);
      if (teamId !== this.teamId) return;
      if (!response.success) throw new Error(response.message || 'Unable to load schedule.');
      this.currentDay = response.data.monthlyTopupDay;
      this.day = response.data.pendingDay ?? this.currentDay;
      this.nextFundingDate = response.data.nextFundingDate;
      this.hasSubscription = response.data.hasSubscription;
      this.pending = response.data.pending;
    } catch (error: any) {
      if (teamId === this.teamId) this.error = this.errorMessage(error);
    } finally {
      if (teamId === this.teamId) this.loading = false;
    }
  }

  async review(): Promise<void> {
    const teamId = this.teamId;
    if (!teamId || !this.day) return;
    this.loading = true;
    this.error = '';
    this.message = '';
    try {
      const response = await this.teams.previewMonthlyTopupSchedule(teamId, this.day);
      if (teamId !== this.teamId) return;
      if (!response.success) throw new Error(response.message || 'Unable to preview schedule.');
      this.preview = response.data;
    } catch (error: any) {
      if (teamId === this.teamId) this.error = this.errorMessage(error);
    } finally {
      if (teamId === this.teamId) this.loading = false;
    }
  }

  async confirm(): Promise<void> {
    const teamId = this.teamId;
    const preview = this.preview;
    if (!preview || !teamId) return;
    this.loading = true;
    this.error = '';
    try {
      const response = await this.teams.changeMonthlyTopupSchedule(teamId, preview.monthlyTopupDay, preview.quoteToken);
      if (teamId !== this.teamId) return;
      if (!response.success) throw new Error(response.message || 'Unable to change schedule.');
      await this.load();
      this.message = 'Monthly top-up day saved.';
    } catch (error: any) {
      if (teamId === this.teamId) this.error = this.errorMessage(error);
    } finally {
      if (teamId === this.teamId) this.loading = false;
    }
  }

  private errorMessage(error: any): string {
    return error?.data?.message || error?.error?.message || error?.message || 'Unable to update the schedule. Reload to check the change before retrying.';
  }
}
